import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
    FlussPageHeader,
    FlussStatusChip,
    InstanceTimeline,
} from "modules/fluss/components/design-kit/FlussDesignKit";
import { FlussRuntimeViewer } from "modules/fluss/components/FlussRuntimeViewer";
import { useFlussCable } from "modules/fluss/hooks/useFlussCable";
import { flussApi } from "modules/fluss/services/fluss-api";
import { flussDefinitionLabel } from "modules/fluss/utils/flussDefinitionLabel";
import { currentNodeLabel } from "modules/fluss/utils/currentNodeLabel";

const FlussRequestDetailPage = () => {
    const { t, i18n } = useTranslation();
    const { instanceId = "" } = useParams();
    const queryClient = useQueryClient();
    const request = useQuery({
        queryKey: ["fluss", "requests", instanceId],
        queryFn: () => flussApi.getRequest(instanceId),
        enabled: Boolean(instanceId),
    });

    useFlussCable({
        instanceId,
        onReceived: () => queryClient.invalidateQueries({ queryKey: ["fluss", "requests", instanceId] }),
    });

    if (!request.data) return <Box className="fluss-page" />;

    const formatDate = (value?: string | null) =>
        value ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—";
    const timeline = request.data.history.map((event) => ({
        id: event.id,
        label: event.event_type === "link.jumped"
            ? t("fluss.event.link_jumped", {
                name: String(event.payload.name || "—"),
                from: String(event.payload.throwElementId || "—"),
                to: String(event.payload.catchElementId || "—"),
                defaultValue: "Link {{name}}: {{from}} → {{to}}",
            })
            : event.event_type === "instance.cancelled" && event.payload.cancel_reason === "terminated"
            ? t("fluss.event.process_terminated", { defaultValue: "Processo encerrado" })
            : t(`fluss.event.${event.event_type.replaceAll(".", "_")}`, {
            defaultValue: t("fluss.event.activity", {
                stage: event.node_label || request.data?.current_node_label || "—",
                defaultValue: "Activity at {{stage}}",
            }),
        }),
        detail: event.payload.error ? String(event.payload.error) : undefined,
        timestamp: formatDate(event.occurred_at),
        tone: event.event_type.includes("incident")
            ? "error" as const
            : event.event_type.includes("completed")
                ? "success" as const
                : "neutral" as const,
    }));

    return (
        <Box className="fluss-page">
            <Stack gap={2}>
                <FlussPageHeader
                    title={flussDefinitionLabel(t, request.data.definition_name)}
                    subtitle={t("fluss.requests.detail_subtitle", { version: request.data.definition_version, defaultValue: "Process version {{version}}" })}
                    status={<FlussStatusChip status={request.data.end_reason === "terminated" ? "terminated" : request.data.status} />}
                />
                <FlussRuntimeViewer xml={request.data.xml} state={request.data.viewer} />
                <Box className="fluss-runtime-detail-grid">
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h6">
                            {t("fluss.requests.progress_title", { defaultValue: "Progress" })}
                        </Typography>
                        <Typography color="text.secondary">
                            {t("fluss.requests.current_stage", { stage: currentNodeLabel(request.data), defaultValue: "Current stage: {{stage}}" })}
                        </Typography>
                        <Typography color="text.secondary">
                            {t("fluss.requests.started_at", { date: formatDate(request.data.started_at), defaultValue: "Started: {{date}}" })}
                        </Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h6">
                            {t("fluss.requests.history_title", { defaultValue: "Activity history" })}
                        </Typography>
                        {timeline.length ? (
                            <InstanceTimeline events={timeline} />
                        ) : (
                            <Alert severity="info">
                                {t("fluss.requests.no_history", { defaultValue: "No process activity has been recorded yet." })}
                            </Alert>
                        )}
                    </Paper>
                </Box>
            </Stack>
        </Box>
    );
};

export default FlussRequestDetailPage;
